import { useAuthStore } from "@/store/authStore";
import { API_BASE_URL } from "@/lib/appEnvironment";
import {
  clearImpersonationAccessToken,
  getActiveAccessToken,
  getImpersonationAccessToken,
  getStoredToken,
  isAccessTokenFresh,
  isImpersonationAccessFresh,
  markImpersonationEnded,
} from "@/lib/auth";
import { queryClient } from "@/lib/queryClient";

type QueryValue = string | number | boolean | null | undefined;
type QueryParams = URLSearchParams | object;

interface ApiRequestConfig {
  params?: QueryParams;
  headers?: HeadersInit;
  data?: unknown;
  responseType?: "json" | "blob";
  signal?: AbortSignal;
  _retried?: boolean;
  _skipAuthRetry?: boolean;
}

interface ApiResponse<T> {
  data: T;
  status: number;
  statusText: string;
  headers: Headers;
  config: ApiRequestConfig;
}

export class ApiClientError<T = unknown> extends Error {
  public readonly response?: ApiResponse<T>;
  public readonly config?: ApiRequestConfig;
  public readonly code?: string;

  constructor(
    message: string,
    options: {
      response?: ApiResponse<T>;
      config?: ApiRequestConfig;
      code?: string;
      cause?: unknown;
    } = {},
  ) {
    super(message);
    this.name = "ApiClientError";
    this.response = options.response;
    this.config = options.config;
    this.code = options.code;
    this.cause = options.cause;
  }
}

export function isApiClientError(error: unknown): error is ApiClientError {
  return error instanceof ApiClientError;
}

/** Stable backend exception class name from ``{ error: "..." }`` payloads. */
export function apiErrorName(error: unknown): string | undefined {
  if (!isApiClientError(error)) return undefined;
  const data = error.response?.data;
  if (data && typeof data === "object" && "error" in data) {
    const name = (data as { error?: unknown }).error;
    return typeof name === "string" ? name : undefined;
  }
  return undefined;
}

type RequestWithRetryFlag = ApiRequestConfig & {
  _retried?: boolean;
};

/** Single in-flight refresh; parallel 401s share one rotation (avoids refresh token reuse races). */
let refreshInFlight: Promise<boolean> | null = null;

class ApiClient {
  private buildUrl(path: string, params?: QueryParams): string {
    const base = API_BASE_URL.replace(/\/+$/, "");
    const normalizedPath = path.startsWith("/") ? path : `/${path}`;
    const rawUrl = /^https?:\/\//i.test(path)
      ? path
      : `${base}${normalizedPath}`;
    const url = /^https?:\/\//i.test(rawUrl)
      ? new URL(rawUrl)
      : new URL(rawUrl, window.location.origin);
    const searchParams =
      params instanceof URLSearchParams ? params : this.toSearchParams(params);
    searchParams.forEach((value, key) => {
      url.searchParams.append(key, value);
    });
    return url.toString();
  }

  private toSearchParams(params?: QueryParams): URLSearchParams {
    const out = new URLSearchParams();
    if (!params || params instanceof URLSearchParams) {
      return params ?? out;
    }

    for (const [key, value] of Object.entries(params) as [
      string,
      QueryValue | QueryValue[],
    ][]) {
      if (Array.isArray(value)) {
        value.forEach((item) => {
          if (item !== undefined && item !== null) {
            out.append(key, String(item));
          }
        });
      } else if (value !== undefined && value !== null) {
        out.append(key, String(value));
      }
    }
    return out;
  }

  private buildHeaders(data: unknown, headers?: HeadersInit): Headers {
    const out = new Headers(headers);
    const token =
      typeof window === "undefined" ? undefined : getActiveAccessToken();

    if (token) {
      out.set("Authorization", `Bearer ${token}`);
    }

    if (data instanceof FormData) {
      out.delete("Content-Type");
    } else if (
      data !== undefined &&
      data !== null &&
      !out.has("Content-Type")
    ) {
      out.set("Content-Type", "application/json");
    }

    return out;
  }

  private buildBody(data: unknown): BodyInit | undefined {
    if (data === undefined || data === null) {
      return undefined;
    }
    if (
      data instanceof FormData ||
      data instanceof Blob ||
      typeof data === "string" ||
      data instanceof URLSearchParams
    ) {
      return data;
    }
    return JSON.stringify(data);
  }

  private async readResponseData<T>(
    response: Response,
    responseType?: ApiRequestConfig["responseType"],
  ): Promise<T> {
    if (responseType === "blob") {
      return (await response.blob()) as T;
    }
    if (response.status === 204) {
      return undefined as T;
    }
    const contentType = response.headers.get("Content-Type") ?? "";
    if (contentType.includes("application/json")) {
      return (await response.json()) as T;
    }
    return (await response.text()) as T;
  }

  private async request<T = never>(
    method: string,
    path: string,
    dataOrConfig?: unknown,
    maybeConfig?: ApiRequestConfig,
  ): Promise<ApiResponse<T>> {
    const methodHasBodyArgument = ["POST", "PUT", "PATCH"].includes(method.toUpperCase());
    const config: RequestWithRetryFlag =
      (methodHasBodyArgument
        ? maybeConfig
        : (dataOrConfig as ApiRequestConfig | undefined)) ?? {};
    const data = methodHasBodyArgument ? dataOrConfig : config.data;
    const requestConfig = { ...config, data };

    try {
      const url = this.buildUrl(path, config.params);
      const response = await fetch(url, {
        method,
        headers: this.buildHeaders(data, config.headers),
        body: this.buildBody(data),
        credentials: "include",
        signal: config.signal,
      });

      let responseData: T;
      try {
        responseData = await this.readResponseData<T>(
          response,
          config.responseType,
        );
      } catch (parseError) {
        // Keep HTTP status for error mapping — do not collapse to ERR_NETWORK.
        if (!response.ok) {
          const apiResponse: ApiResponse<T> = {
            data: undefined as T,
            status: response.status,
            statusText: response.statusText,
            headers: response.headers,
            config: requestConfig,
          };
          throw new ApiClientError(
            response.statusText ||
              `Request failed with status code ${response.status}`,
            { response: apiResponse, config: requestConfig, cause: parseError },
          );
        }
        throw parseError;
      }

      const apiResponse: ApiResponse<T> = {
        data: responseData,
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
        config: requestConfig,
      };

      if (response.ok) {
        return apiResponse;
      }

      throw new ApiClientError(
        response.statusText ||
          `Request failed with status code ${response.status}`,
        { response: apiResponse, config: requestConfig },
      );
    } catch (error) {
      if (isApiClientError(error)) {
        const status = error.response?.status;
        const shouldRetry =
          status === 401 &&
          !config._retried &&
          !config._skipAuthRetry &&
          typeof window !== "undefined" &&
          !path.includes("/auth/login") &&
          !path.includes("/auth/refresh") &&
          !isImpersonationAccessFresh();

        if (!shouldRetry) {
          throw error;
        }

        let shouldReplay: boolean;
        try {
          shouldReplay = await queueTokenRefresh();
        } catch {
          useAuthStore.getState().logout();
          throw error;
        }
        if (!shouldReplay) {
          throw error;
        }
        return this.request<T>(method, path, data, {
          ...config,
          _retried: true,
        });
      }

      const code =
        error instanceof DOMException && error.name === "AbortError"
          ? "ECONNABORTED"
          : "ERR_NETWORK";
      throw new ApiClientError("Network Error", {
        config: requestConfig,
        code,
        cause: error,
      });
    }
  }

  get<T = unknown>(
    path: string,
    config?: ApiRequestConfig,
  ): Promise<ApiResponse<T>> {
    return this.request<T>("GET", path, config);
  }

  post<T = unknown>(
    path: string,
    data?: unknown,
    config?: ApiRequestConfig,
  ): Promise<ApiResponse<T>> {
    return this.request<T>("POST", path, data, config);
  }

  put<T = unknown>(
    path: string,
    data?: unknown,
    config?: ApiRequestConfig,
  ): Promise<ApiResponse<T>> {
    return this.request<T>("PUT", path, data, config);
  }

  patch<T = unknown>(
    path: string,
    data?: unknown,
    config?: ApiRequestConfig,
  ): Promise<ApiResponse<T>> {
    return this.request<T>("PATCH", path, data, config);
  }

  delete<T = unknown>(
    path: string,
    config?: ApiRequestConfig,
  ): Promise<ApiResponse<T>> {
    return this.request<T>("DELETE", path, config);
  }
}

export const apiClient = new ApiClient();

async function refreshAdminAccessToken(): Promise<void> {
  const { data } = await apiClient.post<{ access_token: string }>(
    "/auth/refresh",
    {},
    {
      params: { cookie: true },
      _skipAuthRetry: true,
    },
  );
  useAuthStore.getState().login(data.access_token);
}

/** Restore the admin session after impersonation ends. Does not revoke the refresh cookie. */
export async function restoreAdminAccessToken(): Promise<void> {
  clearImpersonationAccessToken();
  const adminToken = getStoredToken();
  if (adminToken && isAccessTokenFresh(adminToken)) {
    useAuthStore.getState().initAuth();
    queryClient.clear();
    return;
  }
  await refreshAdminAccessToken();
  queryClient.clear();
}

function queueTokenRefresh(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      const impersonationToken = getImpersonationAccessToken();
      if (impersonationToken !== null) {
        // Reached only when the impersonation JWT is stale (see shouldRetry).
        // Restore admin and block Query from replaying this request as admin.
        markImpersonationEnded();
        await restoreAdminAccessToken();
        window.location.replace("/admin/users");
        return false;
      }
      await refreshAdminAccessToken();
      return true;
    })().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}
